'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineClipboardCheck } from '@react-icons/all-files/hi/HiOutlineClipboardCheck';
import { HiOutlineTrendingUp } from '@react-icons/all-files/hi/HiOutlineTrendingUp';
import {
  StyledToolHero,
  StyledToolBody,
  StyledCalculator,
  StyledFieldsGrid,
  StyledFormGroup,
  StyledCalculateButton,
  StyledResult,
  StyledScoreBreakdown,
  StyledScoreRow,
  StyledWidgetsSection,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledRelatedTools,
  StyledRelatedToolGrid,
  StyledRelatedToolCard,
} from './LandingPageGraderTool.styles';

interface IWidget {
  _id: string;
  name: string;
  slug: string;
  logo: string;
  teaser: string;
}

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
  icon?: string;
}

interface IScoreFactor {
  label: string;
  points: number;
  earned: number;
  passed: boolean;
}

interface IToolResult {
  score: number;
  status: 'poor' | 'fair' | 'good';
  factors: IScoreFactor[];
  url: string;
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
}

function getWidgetDescription(slug: string, result: IToolResult): string {
  const score = result.score;

  switch (slug) {
    case 'popup-builder':
      return `Your page scores ${score}/100. Even a well-optimized page loses visitors. Exit-intent popups recover departing visitors and convert them into leads before they are gone.`;
    case 'testimonials':
      return `Social proof is one of the highest-impact conversion elements. Add customer testimonials directly to your landing page to build trust and boost your score.`;
    case 'countdown':
      return `Urgency drives action. A countdown timer on your landing page creates a reason to act now, not later, which directly increases your conversion rate.`;
    case 'trust-badges':
      return `Trust badges (security seals, guarantees, certifications) reduce purchase anxiety and lift conversion rates. Add them above the fold for maximum impact.`;
    default:
      return `Improve your ${score}/100 landing page score with the right on-site conversion tools.`;
  }
}

function getWidgetCTA(slug: string): string {
  switch (slug) {
    case 'popup-builder':
      return 'Add Exit-Intent Popup Free →';
    case 'testimonials':
      return 'Add Testimonials Free →';
    case 'countdown':
      return 'Add Countdown Timer Free →';
    case 'trust-badges':
      return 'Add Trust Badges Free →';
    default:
      return 'Try It Free →';
  }
}

const LOAD_TIME_SCORES: Record<string, number> = {
  '<2s': 15,
  '2-4s': 10,
  '4-6s': 5,
  '>6s': 0,
};

export const LandingPageGraderTool = ({ recommendedWidgets, relatedTools }: IProps) => {
  const [url, setUrl] = useState('');
  const [hasHeadline, setHasHeadline] = useState('');
  const [hasSingleCta, setHasSingleCta] = useState('');
  const [hasSocialProof, setHasSocialProof] = useState('');
  const [hasTrustBadges, setHasTrustBadges] = useState('');
  const [loadTime, setLoadTime] = useState('');
  const [isMobileResponsive, setIsMobileResponsive] = useState('');
  const [hasFormAboveFold, setHasFormAboveFold] = useState('');
  const [result, setResult] = useState<IToolResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef<HTMLDivElement>(null);

  const handleCalculate = () => {
    if (!hasHeadline || !hasSingleCta || !hasSocialProof || !hasTrustBadges || !loadTime || !isMobileResponsive || !hasFormAboveFold) {
      setError('Please answer all questions to grade your landing page.');
      return;
    }

    setError('');

    const factors: IScoreFactor[] = [
      { label: 'Clear Headline', points: 20, earned: hasHeadline === 'yes' ? 20 : 0, passed: hasHeadline === 'yes' },
      { label: 'Single Call to Action', points: 20, earned: hasSingleCta === 'yes' ? 20 : 0, passed: hasSingleCta === 'yes' },
      { label: 'Mobile Responsive', points: 15, earned: isMobileResponsive === 'yes' ? 15 : 0, passed: isMobileResponsive === 'yes' },
      { label: 'Page Load Time', points: 15, earned: LOAD_TIME_SCORES[loadTime] ?? 0, passed: loadTime === '<2s' || loadTime === '2-4s' },
      { label: 'Social Proof', points: 15, earned: hasSocialProof === 'yes' ? 15 : 0, passed: hasSocialProof === 'yes' },
      { label: 'Trust Badges', points: 10, earned: hasTrustBadges === 'yes' ? 10 : 0, passed: hasTrustBadges === 'yes' },
      { label: 'Form Above the Fold', points: 5, earned: hasFormAboveFold === 'yes' ? 5 : 0, passed: hasFormAboveFold === 'yes' },
    ];

    const score = factors.reduce((sum, f) => sum + f.earned, 0);

    let status: 'poor' | 'fair' | 'good';
    if (score >= 80) {
      status = 'good';
    } else if (score >= 50) {
      status = 'fair';
    } else {
      status = 'poor';
    }

    setResult({ score, status, factors, url });
    setHasCalculated(true);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const statusLabel =
    result?.status === 'good'
      ? 'Strong Page'
      : result?.status === 'fair'
        ? 'Needs Work'
        : 'Needs Fixes';

  return (
    <>
      <StyledToolHero>
        <h1>Free <span className="highlight">Landing Page Grader</span></h1>
        <p>
          Score your landing page across 7 proven conversion factors. Find exactly what
          is hurting your results and get actionable fixes. Free, no sign-up required.
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledCalculator>
          <h2>Grade Your Landing Page</h2>

          <StyledFieldsGrid>
            <StyledFormGroup className="full-width">
              <label htmlFor="url">Landing Page URL (optional)</label>
              <input
                id="url"
                type="url"
                placeholder="e.g. https://yoursite.com/landing"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <p className="hint">Enter your page URL for reference (not required for scoring)</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="hasHeadline">Does the page have a clear, benefit-focused headline?</label>
              <select id="hasHeadline" value={hasHeadline} onChange={(e) => setHasHeadline(e.target.value)}>
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <p className="hint">The headline should immediately communicate the value of your offer</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="hasSingleCta">Does the page have a single, prominent CTA?</label>
              <select id="hasSingleCta" value={hasSingleCta} onChange={(e) => setHasSingleCta(e.target.value)}>
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <p className="hint">One primary action removes decision fatigue and boosts conversions</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="hasSocialProof">Does the page include social proof (testimonials, reviews, case studies)?</label>
              <select id="hasSocialProof" value={hasSocialProof} onChange={(e) => setHasSocialProof(e.target.value)}>
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <p className="hint">Social proof reduces buyer hesitation and increases trust</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="hasTrustBadges">Does the page display trust badges (security seals, guarantees, certifications)?</label>
              <select id="hasTrustBadges" value={hasTrustBadges} onChange={(e) => setHasTrustBadges(e.target.value)}>
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <p className="hint">Trust badges lower purchase anxiety and lift conversion rates</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="loadTime">Approximate page load time</label>
              <select id="loadTime" value={loadTime} onChange={(e) => setLoadTime(e.target.value)}>
                <option value="">Select load time</option>
                <option value="<2s">Under 2 seconds</option>
                <option value="2-4s">2 to 4 seconds</option>
                <option value="4-6s">4 to 6 seconds</option>
                <option value=">6s">Over 6 seconds</option>
              </select>
              <p className="hint">Use Google PageSpeed Insights to check your actual load time</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="isMobileResponsive">Is the page fully mobile responsive?</label>
              <select id="isMobileResponsive" value={isMobileResponsive} onChange={(e) => setIsMobileResponsive(e.target.value)}>
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <p className="hint">Over 60% of web traffic comes from mobile devices</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="hasFormAboveFold">Is the lead capture form visible above the fold without scrolling?</label>
              <select id="hasFormAboveFold" value={hasFormAboveFold} onChange={(e) => setHasFormAboveFold(e.target.value)}>
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <p className="hint">Forms above the fold see higher submission rates than those requiring scrolling</p>
            </StyledFormGroup>
          </StyledFieldsGrid>

          {error && (
            <p style={{ color: '#e22029', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</p>
          )}

          <StyledCalculateButton onClick={handleCalculate}>
            Grade My Landing Page
          </StyledCalculateButton>
        </StyledCalculator>

        {hasCalculated && result && (
          <>
            <StyledResult status={result.status} ref={resultRef}>
              <p className="result-label">Your Landing Page Score</p>
              <p className="result-number">{result.score}<span className="result-denom">/100</span></p>
              <span className="result-badge">{statusLabel}</span>

              {result.url && (
                <div className="result-benchmark">
                  <p>Score for: <strong>{result.url}</strong></p>
                </div>
              )}

              <StyledScoreBreakdown>
                {result.factors.map((f) => (
                  <StyledScoreRow key={f.label} passed={f.passed}>
                    <span className="factor-status">{f.passed ? '✓' : '✗'}</span>
                    <span className="factor-label">{f.label}</span>
                    <span className="factor-points">{f.earned}/{f.points} pts</span>
                  </StyledScoreRow>
                ))}
              </StyledScoreBreakdown>

              {result.status === 'poor' && (
                <div className="result-pain">
                  <strong>Your landing page has critical gaps:</strong> A score below 50 means you are likely losing a significant portion of visitors who could convert. Focus first on adding a clear headline and a single CTA, then address mobile responsiveness and page speed. These four fixes alone could double your conversion rate.
                </div>
              )}

              {result.status === 'fair' && (
                <div className="result-pain">
                  <strong>Your page is working but leaving conversions behind:</strong> Scores between 50 and 80 mean you have the basics covered but you are missing elements that separates average pages from top performers. Adding social proof, trust badges, and a faster load time are typically the highest-impact next steps.
                </div>
              )}

              {result.status === 'good' && (
                <div className="result-pain">
                  <strong>Your landing page covers the conversion essentials:</strong> A score of 80 or above means your page is well-optimized. To squeeze out more conversions, focus on A/B testing your headline and CTA copy, adding exit-intent popups, and using urgency elements like countdown timers.
                </div>
              )}
            </StyledResult>

            <StyledWidgetsSection>
              <h2>Boost Your Landing Page Conversion Rate</h2>
              <p className="subtitle">
                These widgets help thousands of businesses turn more landing page visitors into leads and customers.
              </p>
              <StyledWidgetGrid>
                {recommendedWidgets.map((widget) => (
                  <StyledWidgetCard
                    key={widget._id}
                    href={`/widgets/${widget.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="widget-header">
                      {widget.logo && (
                        <Image
                          src={widget.logo}
                          alt={`${widget.name} logo`}
                          width={48}
                          height={48}
                        />
                      )}
                      <h3>{widget.name}</h3>
                    </div>
                    <p>{getWidgetDescription(widget.slug, result)}</p>
                    <span className="widget-cta">{getWidgetCTA(widget.slug)}</span>
                  </StyledWidgetCard>
                ))}
              </StyledWidgetGrid>
            </StyledWidgetsSection>
          </>
        )}

        {relatedTools.length > 0 && (
          <StyledRelatedTools>
            <h2>Explore More Free Lead Generation Tools</h2>
            <p className="subtitle">
              Other free calculators to help you benchmark and grow your lead generation.
            </p>
            <StyledRelatedToolGrid>
              {relatedTools.map((tool) => (
                <StyledRelatedToolCard
                  key={tool.slug}
                  as={Link}
                  href={`/free-tools/lead-generation/${tool.slug}`}
                >
                  <span className="tool-icon">
                    <HiOutlineTrendingUp />
                  </span>
                  <h3>{tool.name}</h3>
                  <p>{tool.description}</p>
                  <span className="tool-cta">Use Tool →</span>
                </StyledRelatedToolCard>
              ))}
            </StyledRelatedToolGrid>
          </StyledRelatedTools>
        )}

      </StyledToolBody>
    </>
  );
};
